// src/components/Admin/QuestionsPending.jsx
import React, { useState, useEffect } from "react";
import {
  Clock,
  User,
  Calendar,
  Tag,
  CheckCircle,
  XCircle,
} from "lucide-react";

import API_BASE_URL from "../../services/config";

import { authFetch } from "../../auth";
import AnswerModal from "./AnswerModal";

const QuestionsPending = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchPendingQuestions();
  }, []);

  const fetchPendingQuestions = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/admin/questions/pending`,
        {
          method: "GET",
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to load questions (${res.status})`);
      }

      const data = await res.json();
      setQuestions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching pending questions:", err);
      setError(err.message || "Failed to load pending questions.");
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSubmit = async (answerData) => {
    if (!selectedQuestion) return;

    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/admin/questions/${selectedQuestion.id}/answer`,
        {
          method: "POST",
          body: JSON.stringify(answerData),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg =
          body.message ||
          body.error ||
          `Failed to submit answer (${res.status})`;
        throw new Error(msg);
      }

      setSelectedQuestion(null);
      await fetchPendingQuestions(); // Refresh list
    } catch (err) {
      console.error("Error submitting answer:", err);
      alert(err.message || "Failed to submit answer. Please try again.");
    }
  };

  // Optional: hook up dismiss to a backend endpoint if you have one
  const handleDismiss = async (question) => {
    // If you later add an endpoint like:
    // PATCH /api/admin/questions/{id}/status with { status: "DISMISSED" }
    // you can call it here with authFetch.
    console.log("Dismiss clicked for question:", question.id);
    // For now just log; no data change.
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "HIGH":
        return "bg-red-100 text-red-800";
      case "MEDIUM":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Personal Growth": "bg-purple-100 text-purple-800",
      "Daily Life & Dharma": "bg-blue-100 text-blue-800",
      "Philosophy & Spiritual Growth": "bg-green-100 text-green-800",
      "Career & Duty": "bg-indigo-100 text-indigo-800",
      "Festivals & Rituals": "bg-pink-100 text-pink-800",
    };
    return colors[category] || "bg-gray-100 text-gray-800";
  };

  const formatDate = (value) => {
    if (!value) return "Unknown";
    const d = new Date(value);
    return isNaN(d.getTime()) ? "Unknown" : d.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Pending Questions
            </h1>
            <p className="text-gray-600 mt-1">
              Questions waiting for your scriptural guidance
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
              <Clock className="h-4 w-4 mr-1" />
              {questions.length} Pending
            </span>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
            {error}
          </div>
        )}
      </div>

      {/* Questions Grid */}
      <div className="grid grid-cols-1 gap-6">
        {questions.map((question) => (
          <div
            key={question.id}
            className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {question.title}
                    </h3>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(
                        question.priority
                      )}`}
                    >
                      {question.priority || "NORMAL"}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {question.body}
                  </p>

                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                    <span
                      className={`px-3 py-1 rounded-full ${getCategoryColor(
                        question.category
                      )}`}
                    >
                      <Tag className="h-3 w-3 inline mr-1" />
                      {question.category || "Uncategorized"}
                    </span>
                    <span className="flex items-center">
                      <User className="h-3 w-3 mr-1" />
                      {question.user?.name || "Anonymous"}
                    </span>
                    <span className="flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {formatDate(question.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="ml-4 flex flex-col space-y-2">
                  <button
                    onClick={() => setSelectedQuestion(question)}
                    className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Answer
                  </button>
                  <button
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
                    onClick={() => handleDismiss(question)}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Empty state */}
        {questions.length === 0 && !error && (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <Clock className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              No pending questions
            </h3>
            <p className="mt-2 text-gray-600 max-w-md mx-auto">
              All questions have been answered! Check back later for new
              questions.
            </p>
          </div>
        )}
      </div>

      {/* Answer Modal */}
      {selectedQuestion && (
        <AnswerModal
          question={selectedQuestion}
          onClose={() => setSelectedQuestion(null)}
          onSubmit={handleAnswerSubmit}
        />
      )}
    </div>
  );
};

export default QuestionsPending;
